/**
 * Provisioning wait page (ADR-0041 Fase 1) — the `success_url` Stripe redirects to after
 * a paid checkout. Pay-then-provision means the tenant is created ASYNC by the
 * `checkout.session.completed` webhook, so this page polls `checkout/status?token=` until
 * provisioning lands, then hard-redirects into the new tenant's login. Expired / canceled
 * / unknown → a friendly retry back to the wizard.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { onboardingApi } from "@/lib/api-onboarding";

const POLL_MS = 2500;
// Provisioning is often near-instant now (the success-page poll self-heals), so without a
// floor the wait screen would flash by and the user wouldn't grasp that a workspace was
// just created. Hold it at least this long before redirecting into the new tenant.
const MIN_VISIBLE_MS = 5000;
const SIGNUP_PATH = "/onboarding";

export function ProvisioningPage() {
  const { t } = useTranslation();
  const token =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("token") : null;
  const [failed, setFailed] = React.useState(!token);
  const [ready, setReady] = React.useState(false); // provisioned — redirecting in a moment
  const startedAt = React.useRef(Date.now());

  React.useEffect(() => {
    if (!token) return;
    let alive = true;
    let timer = 0;
    function redirectSoon(url: string) {
      setReady(true); // swap the copy to "ready, taking you in…"
      const elapsed = Date.now() - startedAt.current;
      timer = window.setTimeout(
        () => {
          if (alive) window.location.href = url; // into the new tenant
        },
        Math.max(0, MIN_VISIBLE_MS - elapsed), // honour the minimum dwell time
      );
    }
    async function poll() {
      try {
        const res = await onboardingApi.checkoutStatus(token!);
        if (!alive) return;
        if (res.status === "completed" && res.redirect_url) {
          redirectSoon(res.redirect_url);
          return;
        }
        if (res.status === "expired" || res.status === "canceled" || res.status === "not_found") {
          setFailed(true);
          return;
        }
        timer = window.setTimeout(poll, POLL_MS); // still pending
      } catch {
        if (alive) timer = window.setTimeout(poll, POLL_MS * 2);
      }
    }
    poll();
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [token]);

  return (
    <div className="grid min-h-screen place-items-center bg-background p-4">
      <div className="w-full max-w-md rounded-xl border border-border/60 bg-surface p-8 text-center shadow-lg">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <img src="/tray-logo.svg" alt="" aria-hidden="true" className="h-7 w-7 shrink-0" />
          <span className="text-base font-semibold tracking-tight">Tray</span>
        </div>
        {failed ? (
          <>
            <Alert
              tone="danger"
              title={t("onboarding.provisioning.failedTitle", "Qualcosa non ha funzionato")}
            >
              {t(
                "onboarding.provisioning.failed",
                "Non siamo riusciti a completare la creazione. Se il pagamento è andato a buon fine, contattaci; altrimenti riprova.",
              )}
            </Alert>
            <Button className="mt-5 w-full" onClick={() => (window.location.href = SIGNUP_PATH)}>
              {t("onboarding.provisioning.retry", "Torna alla registrazione")}
            </Button>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
            <h1 className="mt-4 text-lg font-semibold text-foreground">
              {ready
                ? t("onboarding.provisioning.readyTitle", "Workspace pronto!")
                : t("onboarding.provisioning.title", "Stiamo creando il tuo workspace…")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {ready
                ? t("onboarding.provisioning.readySubtitle", "Ti portiamo al tuo nuovo spazio…")
                : t(
                    "onboarding.provisioning.subtitle",
                    "Ci vuole solo qualche secondo. Non chiudere questa pagina.",
                  )}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
